
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Code, Copy, Eye, Mail, RefreshCw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getTemplateForSendGrid, getTemplateDocumentation } from '@/emails/utils/emailTemplateHelpers';
import AdminLayout from './AdminLayout';

type TemplateType = 'user-registration' | 'password-reset' | 'notification' | 'report-sending';

const EmailTemplates = () => {
  const [activeTab, setActiveTab] = useState<TemplateType>('user-registration');
  const [viewMode, setViewMode] = useState<'preview' | 'code'>('preview');
  const [copied, setCopied] = useState(false);

  // Get the template HTML based on current tab
  const templateHtml = getTemplateForSendGrid(activeTab);
  
  // Get the template documentation
  const documentation = getTemplateDocumentation(activeTab);

  // Handle copy to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(templateHtml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const templateNames = {
    'user-registration': 'User Registration',
    'password-reset': 'Password Reset',
    'notification': 'Event Notification',
    'report-sending': 'Report Sending'
  };

  return (
    <AdminLayout>
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Email Templates</h1>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setViewMode(viewMode === 'preview' ? 'code' : 'preview')}
            >
              {viewMode === 'preview' ? <Code className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              {viewMode === 'preview' ? 'View Code' : 'View Preview'}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy HTML
                </>
              )}
            </Button>
          </div>
        </div>

        <Tabs 
          value={activeTab} 
          onValueChange={(value) => setActiveTab(value as TemplateType)}
          className="space-y-4"
        >
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="user-registration">Registration</TabsTrigger>
            <TabsTrigger value="password-reset">Password Reset</TabsTrigger>
            <TabsTrigger value="notification">Notification</TabsTrigger>
            <TabsTrigger value="report-sending">Report</TabsTrigger>
          </TabsList>

          {/* Template info and content for all tabs */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-1 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Mail className="h-5 w-5 mr-2" />
                    {templateNames[activeTab]}
                  </CardTitle>
                  <CardDescription>SendGrid Template</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium mb-2">Description</h3>
                      <p className="text-sm text-muted-foreground">{documentation.description}</p>
                    </div>
                    
                    <div>
                      <h3 className="font-medium mb-2">Required Variables</h3>
                      <ul className="list-disc list-inside text-sm">
                        {documentation.requiredVariables.map((variable) => (
                          <li key={variable} className="text-muted-foreground">{variable}</li>
                        ))}
                      </ul>
                    </div>

                    {documentation.optionalVariables.length > 0 && (
                      <div>
                        <h3 className="font-medium mb-2">Optional Variables</h3>
                        <ul className="list-disc list-inside text-sm">
                          {documentation.optionalVariables.map((variable) => (
                            <li key={variable} className="text-muted-foreground">{variable}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>PHP Usage Example</CardTitle>
                  <CardDescription>How to use this template</CardDescription>
                </CardHeader>
                <CardContent>
                  <pre className="p-4 bg-secondary rounded-md overflow-x-auto text-xs">
                    <code>{documentation.samplePhpCode}</code>
                  </pre>
                </CardContent>
              </Card>
            </div>

            <div className="md:col-span-2">
              <Card className="h-full">
                <CardHeader>
                  <CardTitle>
                    {viewMode === 'preview' ? 'Template Preview' : 'HTML Code'}
                  </CardTitle>
                  <CardDescription>
                    {viewMode === 'preview' 
                      ? 'How the email will appear to recipients' 
                      : 'HTML code for SendGrid Dynamic Templates'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {viewMode === 'preview' ? (
                    <div className="border rounded-md overflow-hidden h-[600px]">
                      <iframe
                        title={`${activeTab} email template`}
                        srcDoc={templateHtml}
                        className="w-full h-full"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  ) : (
                    <pre className="p-4 bg-secondary rounded-md overflow-auto h-[600px] text-xs">
                      <code>{templateHtml}</code>
                    </pre>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </Tabs>

        <Alert className="mt-6">
          <RefreshCw className="h-4 w-4" />
          <AlertDescription>
            After copying this HTML, go to SendGrid &gt; Marketing &gt; Dynamic Templates and create a new template with this HTML.
          </AlertDescription>
        </Alert>
      </div>
    </AdminLayout>
  );
};

export default EmailTemplates;
